// ============================================================
// GameBrief Assembler — Task 1.5
// Combines metadata, phases, and key moments into the
// GameBrief structure that gets sent to the LLM.
// ============================================================

import type {
  GameMetadata,
  MoveData,
  GameBrief,
  GamePhase,
  KeyMoment,
  GameArc,
} from "@shared/types"

// --- Public API ---

export interface AssemblerInput {
  metadata: GameMetadata
  moves: MoveData[]
  phases: GamePhase[]
  keyMoments: KeyMoment[]
  gameArc: GameArc
}

/**
 * Assemble a GameBrief from the pipeline outputs.
 * The GameBrief is what gets sent to the LLM for narration.
 * It should be ~800-1500 tokens when serialized.
 */
export function assembleGameBrief(input: AssemblerInput): GameBrief {
  const { metadata, phases, keyMoments, gameArc } = input

  return {
    gameMetadata: {
      white: formatPlayer(metadata.white, metadata.whiteElo),
      black: formatPlayer(metadata.black, metadata.blackElo),
      result: metadata.result,
      timeControl: metadata.timeControl,
      opening: metadata.opening,
      totalMoves: metadata.totalMoves,
    },
    gameArc,
    phases: condensePhasesForBrief(phases),
    keyMoments: condenseKeyMomentsForBrief(keyMoments),
    narrativeInstructions: {
      tone: "casual-insightful",
      audience: "visual chess players who don't read notation",
      noNotation: true,
      referToPiecesBy: "name and location",
      focus: "tell the story of the game, not analyze individual moves",
    },
  }
}

// --- Formatting helpers ---

function formatPlayer(name: string, elo: number | null): string {
  if (elo) return `${name} (${elo})`
  return name
}

/**
 * Condense phases to fit within the token budget.
 * Merge adjacent phases of the same type if there are too many.
 */
function condensePhasesForBrief(phases: GamePhase[]): GamePhase[] {
  if (phases.length <= 8) return phases

  // Merge adjacent middlegame phases
  const merged: GamePhase[] = []

  for (const phase of phases) {
    const prev = merged[merged.length - 1]

    if (
      prev &&
      prev.type === "middlegame" &&
      phase.type === "middlegame"
    ) {
      // Merge into previous
      prev.moveEnd = phase.moveEnd
      prev.moves = `${prev.moveStart}-${phase.moveEnd}`
      prev.evalRange = [
        Math.min(prev.evalRange[0], phase.evalRange[0]),
        Math.max(prev.evalRange[1], phase.evalRange[1]),
      ]
      prev.summary = `${prev.summary} ${phase.summary}`
    } else {
      merged.push({ ...phase })
    }
  }

  // If still too many, drop result phases and merge more aggressively
  if (merged.length > 10) {
    return merged.filter((p) => p.type !== "result").slice(0, 10)
  }

  return merged
}

/**
 * Condense key moments for the brief.
 * Keep only the most important moments and trim descriptions.
 */
function condenseKeyMomentsForBrief(moments: KeyMoment[]): KeyMoment[] {
  // Already limited to 8 by key-moments module, but double check
  const limited = moments.slice(0, 6)

  // Ensure descriptions are concise (under 150 chars)
  return limited.map((m) => ({
    ...m,
    description:
      m.description.length > 150
        ? m.description.substring(0, 147) + "..."
        : m.description,
    bestMoveDescription:
      m.bestMoveDescription && m.bestMoveDescription.length > 100
        ? m.bestMoveDescription.substring(0, 97) + "..."
        : m.bestMoveDescription,
  }))
}

/**
 * Estimate the token count of a serialized GameBrief.
 * Rough heuristic: ~4 characters per token for JSON.
 */
export function estimateBriefTokens(brief: GameBrief): number {
  const json = JSON.stringify(brief)
  return Math.ceil(json.length / 4)
}

/**
 * Validate that a GameBrief has all required fields and makes sense.
 */
export function validateGameBrief(brief: GameBrief): string[] {
  const errors: string[] = []

  if (!brief.gameMetadata.white) errors.push("Missing white player name")
  if (!brief.gameMetadata.black) errors.push("Missing black player name")
  if (!brief.gameMetadata.result) errors.push("Missing game result")
  if (brief.gameMetadata.totalMoves <= 0)
    errors.push("Invalid total moves count")

  if (brief.phases.length === 0) errors.push("No phases detected")
  if (!brief.gameArc) errors.push("No game arc classified")

  // Check phase continuity
  for (let i = 1; i < brief.phases.length; i++) {
    const prev = brief.phases[i - 1]
    const curr = brief.phases[i]
    if (curr.moveStart > prev.moveEnd + 1) {
      // Allow small gaps (1 move) from phase boundary rounding
      if (curr.moveStart > prev.moveEnd + 2) {
        errors.push(
          `Gap in phases: phase ${i - 1} ends at move ${prev.moveEnd}, phase ${i} starts at move ${curr.moveStart}`
        )
      }
    }
  }

  // Check key moments reference valid moves
  for (const moment of brief.keyMoments) {
    if (moment.moveNumber < 1) {
      errors.push(`Key moment has invalid move number: ${moment.moveNumber}`)
    }
    if (moment.moveNumber > brief.gameMetadata.totalMoves) {
      errors.push(
        `Key moment move ${moment.moveNumber} exceeds total moves ${brief.gameMetadata.totalMoves}`
      )
    }
  }

  return errors
}
